import React, { useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useNavigate, useOutletContext } from "react-router-dom";
import BackArrow from "../../assets/icons/left-pointing-arrow-pink.svg";
import scheduleIcon from "../../assets/icons/schedule-icon.svg";
import { useStickyHeader } from "../../shared/hooks/StickyHeader";
import { BorderSecondary, PrimaryButton } from "../../utils/muiCustomTheme";
import Box from "@mui/material/Box";
import { CircularProgressCustom } from "../../utils/muiCustomTheme";
import DialogComponent from "../createSegment/DialogComponent";
import DestinationPickList from "./DestinationsPickLists";
import { CreateDestinationProps } from "../destinations/destination-interface";
import Fb from "../createDestinations/Fb";
import Dv360 from "./Dv360";
import Tiktok from "./Tiktok";
import Amazon from "./Amazon";
import {useAppMe} from "../../services/okta";

import { useCreateDestination } from "../../services/destination";
import { OutletContext } from "../../App";

import "./CreateDestinations.scss";
import { useSimulationParams } from "../../shared/hooks/useSimulationParams";

const CreateDestination: React.FC<CreateDestinationProps> = () => {
  const { setAlert } = useOutletContext<OutletContext>();

  const navigate = useNavigate();
  const { headerRef, sentinelRef, isSticky } = useStickyHeader();
  const { createLink } = useSimulationParams();
  const [showUnsavedWorkDialog, setShowUnsavedWorkDialog] = useState(false);
  const { data: dataAppMe, isPending: isPendingAppMe } = useAppMe({});
  const userEmail = dataAppMe?.data?.user_id;

  const methods = useForm<any>({
    defaultValues: {
      country: "",
      brand: "",
      destination: "",
      email: userEmail,
      version_name: "",
    },
  });
  const { handleSubmit, watch, formState } = methods;
  const { isDirty } = formState;

  const { mutate: createDestination, isPending: isPendingCreate } = useCreateDestination({
    options: {
      onSuccess: (res: any) => {
        let isSuccess = res?.data.status.toLowerCase() === "success";
        if (isSuccess) {
          navigate(createLink("/destinations"));
        } else {
          setAlert({
            alertType: "warning",
            alertMsg: "Saving failed",
          });
        }
      },
    },
  });

  const closeUnsavedWorkDialog = () => setShowUnsavedWorkDialog(false);

  const handleDiscard = () => {
    if (isDirty) {
      setShowUnsavedWorkDialog(true);
    } else {
      navigate(createLink("/destinations"));
    }
  };

  const onSubmit = (data: any) => {
    createDestination(data);
  };

  const destination = watch("destination");

  if (isPendingAppMe) { // Show loader until form defaults are set
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100vh" }}>
        <CircularProgressCustom />
      </Box>
    );
  }

  return (
    <FormProvider {...methods}>
      <form>
        <div className="create-destination">
          <div ref={sentinelRef}></div>
          <div
            ref={headerRef}
            className={`create-destination__wrap ${isSticky ? "is-sticky" : ""}`}
          >
            <div className="create-destination__wrap__heading-wrap" onClick={handleDiscard}>
              <div className="create-destination__wrap__heading-wrap__icon">
                <img src={BackArrow} alt="Back" />
              </div>
              <div className="create-destination__wrap__heading-wrap__heading">Back to Destinations</div>
            </div>
            <div className="create-destination__wrap__btn-wrap">
              <div className="create-destination__wrap__btn-wrap__label">
                Authorize destination
              </div>
              <div className="create-destination__wrap__btn">
                <BorderSecondary type="button" onClick={handleDiscard}>
                  Discard
                </BorderSecondary>
                <PrimaryButton
                  variant="contained"
                  onClick={handleSubmit(onSubmit)}
                  endIcon={
                    <img
                      className="btn-primary__icon"
                      src={scheduleIcon}
                      alt="Authorize destination"
                    />
                  }
                >
                  {isPendingCreate ? "Loading..." : "Save"}
                </PrimaryButton>
              </div>
            </div>
          </div>
          <div className="create-destination__divider"></div>
          <div className="create-destination__wrap">
            <DestinationPickList />

            {destination === "Meta" && (<Fb />)}
            {destination === "Dv360" && (<Dv360 />)}
            {destination === "Tiktok" && (<Tiktok />)}
            {destination === "Amazon" && (<Amazon />)}
          </div>
        </div>

        <DialogComponent open={showUnsavedWorkDialog} onClose={closeUnsavedWorkDialog} />
      </form>
    </FormProvider>
  );
};

export default CreateDestination;
